import Vue from 'vue'
import { userAxios } from '@/core/services/api'

const DOCTOR_ROLE_ID = 3
const QA_STORAGE_KEY = 'performanceAlertQa'

const state = Vue.observable({
  visible: false,
  loading: false,
  signing: false,
  alertData: null,
  alertContent: '',
})

let inflightRequest = null
let listenersInitialized = false

const isQaEnabled = () => {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(QA_STORAGE_KEY) === '1'
}

const qaLog = (message, data) => {
  if (!isQaEnabled()) return
  // eslint-disable-next-line no-console
  console.info(`[PerformanceAlert QA] ${message}`, data)
}

const normalizeAlertPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null
  if (payload.data && typeof payload.data === 'object') return payload.data
  return payload
}

const parseUser = () => {
  const rawUser = localStorage.getItem('userData')
  if (!rawUser) return null
  try {
    return JSON.parse(rawUser)
  } catch (error) {
    return null
  }
}

const hasToken = () => Boolean(localStorage.getItem('accessToken'))

const isDoctorUser = () => {
  const userData = parseUser()
  const roles = Array.isArray(userData?.roles) ? userData.roles : []
  return roles.some((role) => Number(role?.id) === DOCTOR_ROLE_ID)
}

const isPresent = (value) => value !== null && value !== undefined && value !== ''

const money = (value) => {
  if (!isPresent(value)) return ''
  const amount = Number(value)
  if (!Number.isFinite(amount)) return String(value)
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const visitCountLabel = (count) => {
  if (!isPresent(count)) return ''
  const total = Number(count)
  if (!Number.isFinite(total)) return ''
  return total === 1 ? '1 visita' : `${total} visitas`
}

const visitTypeLabel = (typeId) => {
  if (Number(typeId) === 3) return 'NP'
  if (Number(typeId) === 2) return 'NC'
  if (isPresent(typeId)) return `Tipo ${typeId}`
  return ''
}

const metricRow = (label, average, count, met) => {
  if (!isPresent(average)) return null
  const value = [money(average), visitCountLabel(count)].filter(Boolean).join(' · ')
  return {
    label,
    value,
    belowGoal: met === false,
  }
}

const failureMessage = (data) => {
  const goal = money(data?.goal)
  const parts = []
  if (data?.np_met === false && isPresent(data?.avg_np)) {
    parts.push(`Avg NP (${money(data.avg_np)}) está por debajo de la meta (${goal})`)
  }
  if (data?.nc_met === false && isPresent(data?.avg_nc)) {
    parts.push(`Avg NC (${money(data.avg_nc)}) está por debajo de la meta (${goal})`)
  }
  if (!parts.length) return ''
  return `${parts.join('. ')}. Se requiere firma de reconocimiento.`
}

const patientLines = (patientsSummary) => {
  if (!Array.isArray(patientsSummary) || patientsSummary.length === 0) return []

  return patientsSummary.map((patient, index) => {
    if (typeof patient === 'string') return `${index + 1}. ${patient}`
    if (!patient || typeof patient !== 'object') {
      return `${index + 1}. ${isPresent(patient) ? String(patient) : '-'}`
    }
    const name =
      patient.name ||
      patient.full_name ||
      `${patient.first_name || ''} ${patient.last_name || ''}`.trim()
    const amount = patient.billed ?? patient.total ?? patient.amount
    const bits = [name, visitTypeLabel(patient.type_visits_id), isPresent(amount) ? money(amount) : ''].filter(Boolean)
    return `${index + 1}. ${bits.join(' · ') || '-'}`
  })
}

const buildAlertView = (data) => {
  const rows = []
  if (isPresent(data?.date)) rows.push({ label: 'Fecha del día evaluado', value: String(data.date) })
  if (isPresent(data?.goal)) rows.push({ label: 'Meta', value: money(data.goal) })
  if (isPresent(data?.target)) rows.push({ label: 'Objetivo', value: money(data.target) })

  const npRow = metricRow('Promedio NP', data?.avg_np, data?.np_count, data?.np_met)
  const ncRow = metricRow('Promedio NC', data?.avg_nc, data?.nc_count, data?.nc_met)
  if (npRow) rows.push(npRow)
  if (ncRow) rows.push(ncRow)

  if (isPresent(data?.patients_count)) {
    rows.push({ label: 'Pacientes atendidos', value: String(data.patients_count) })
  }
  if (isPresent(data?.total_billed)) {
    rows.push({ label: 'Total facturado', value: money(data.total_billed) })
  }

  const message = isPresent(data?.alert_message) ? String(data.alert_message) : failureMessage(data)

  return {
    message,
    rows,
    patients: patientLines(data?.patients_summary),
  }
}

const buildAlertContent = (data) => {
  const view = buildAlertView(data)
  const lines = []
  if (view.message) lines.push(view.message, '')
  view.rows.forEach((row) => {
    const note = row.belowGoal ? ' (por debajo de la meta)' : ''
    lines.push(`${row.label}: ${row.value}${note}`)
  })
  if (view.patients.length) {
    lines.push('', 'Resumen de pacientes:', ...view.patients)
  }
  return lines.join('\n')
}

const applyAlertData = (data) => {
  state.alertData = data || null
  state.alertContent = buildAlertContent(data)
  state.visible = Boolean(data?.requires_signature)
  qaLog('Estado actualizado', {
    requires_signature: Boolean(data?.requires_signature),
    alert_content: state.alertContent,
    raw_response: data,
  })
}

const resetAlert = () => {
  qaLog('Reseteando alerta pendiente', { hadVisibleAlert: state.visible })
  state.visible = false
  state.alertData = null
  state.alertContent = ''
}

const canCheckAlert = () => hasToken() && isDoctorUser()

const refreshPendingAlert = async ({ force = false } = {}) => {
  // Debug temporal solicitado: trazas directas del flujo GET
  // eslint-disable-next-line no-console
  console.log('[PerformanceAlert] refreshPendingAlert:start', {
    force,
    hasToken: hasToken(),
    isDoctor: isDoctorUser(),
    visible: state.visible,
    inflight: Boolean(inflightRequest),
  })

  qaLog('refreshPendingAlert invocado', {
    force,
    hasToken: hasToken(),
    isDoctor: isDoctorUser(),
    alreadyVisible: state.visible,
    hasInflight: Boolean(inflightRequest),
  })

  if (!canCheckAlert()) {
    // eslint-disable-next-line no-console
    console.log('[PerformanceAlert] refreshPendingAlert:skip (sin token o no doctor)')
    resetAlert()
    return null
  }

  if (state.visible && !force) return state.alertData
  if (inflightRequest) return inflightRequest

  state.loading = true
  // eslint-disable-next-line no-console
  console.log('[PerformanceAlert] GET /doctor/performance-alert:request')
  inflightRequest = userAxios
    .get('doctor/performance-alert')
    .then((response) => {
      const envelope = response?.data || null
      const normalizedData = normalizeAlertPayload(envelope)
      // eslint-disable-next-line no-console
      console.log('[PerformanceAlert] GET /doctor/performance-alert:response', {
        raw: envelope,
        normalized: normalizedData,
      })
      qaLog('GET doctor/performance-alert OK', {
        raw: envelope,
        normalized: normalizedData,
      })
      applyAlertData(normalizedData)
      return normalizedData
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log('[PerformanceAlert] GET /doctor/performance-alert:error', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      })
      qaLog('GET doctor/performance-alert ERROR', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      })
      resetAlert()
      return null
    })
    .finally(() => {
      state.loading = false
      inflightRequest = null
    })

  return inflightRequest
}

const signPendingAlert = async (signatureBase64) => {
  if (!state.alertContent) return null
  state.signing = true

  try {
    const payload = {
      signature: signatureBase64,
      alert_content: state.alertContent,
    }
    qaLog('POST doctor/performance-alert/sign payload', payload)
    qaLog('POST payload resumen', {
      signature_length: signatureBase64?.length || 0,
      signature_prefix: signatureBase64?.slice(0, 40) || '',
      alert_content: state.alertContent,
    })
    const response = await userAxios.post('doctor/performance-alert/sign', payload)
    qaLog('POST doctor/performance-alert/sign OK', response?.data || null)
    resetAlert()
    return response?.data || null
  } catch (error) {
    qaLog('POST doctor/performance-alert/sign ERROR', {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data,
    })
    throw error
  } finally {
    state.signing = false
  }
}

const initListeners = () => {
  if (listenersInitialized) return
  listenersInitialized = true

  window.addEventListener('performance-alert:pending', (event) => {
    qaLog('Evento performance-alert:pending recibido', event?.detail || null)
    refreshPendingAlert()
  })

  window.addEventListener('performance-alert:reset', (event) => {
    qaLog('Evento performance-alert:reset recibido', event?.detail || null)
    resetAlert()
  })
}

export default {
  state,
  buildAlertView,
  initListeners,
  refreshPendingAlert,
  signPendingAlert,
  resetAlert,
}
